/**
 * index.ts
 *
 * This file is the entry point for the Notion SDK.
 * It exports the Client class and all the API endpoints.
 *
 * @packageDocumentation
 */

import type { RichTextItemResponseCommon } from "./api-endpoints"

export type {
  AgentBatchParameters,
  AgentBatchResponse,
  ApiColor,
  AppendBlockChildrenParameters,
  AppendBlockChildrenResponse,
  AudioBlockObjectResponse,
  BlockObjectRequest,
  BlockObjectResponse,
  BookmarkBlockObjectResponse,
  BotUserObjectResponse,
  BreadcrumbBlockObjectResponse,
  BulletedListItemBlockObjectResponse,
  ButtonPropertyItemObjectResponse,
  CalloutBlockObjectResponse,
  CancelSessionParameters,
  CancelSessionResponse,
  CheckboxPropertyItemObjectResponse,
  ChildDatabaseBlockObjectResponse,
  ChildPageBlockObjectResponse,
  CodeBlockObjectResponse,
  ColumnBlockObjectResponse,
  ColumnListBlockObjectResponse,
  CommentObjectResponse,
  CompleteFileUploadParameters,
  CompleteFileUploadResponse,
  CreateCommentParameters,
  CreateCommentResponse,
  CreateDatabaseParameters,
  CreateDatabaseResponse,
  CreateDataSourceParameters,
  CreateDataSourceResponse,
  CreateViewParameters,
  CreateViewResponse,
  CreateViewQueryParameters,
  CreateViewQueryResponse,
  DashboardRowResponse,
  DashboardWidgetResponse,
  CreatedByPropertyItemObjectResponse,
  CreatedTimePropertyItemObjectResponse,
  CreateFileUploadParameters,
  CreateFileUploadResponse,
  CreatePageParameters,
  CreatePageResponse,
  DatabaseObjectResponse,
  DataSourceObjectResponse,
  DataSourceViewObjectResponse,
  DatePropertyItemObjectResponse,
  DeleteAgentParameters,
  DeleteAgentResponse,
  DeleteBlockParameters,
  DeleteBlockResponse,
  DeleteCommentParameters,
  DeleteCommentResponse,
  DeletedViewQueryResponse,
  DeleteViewParameters,
  DeleteViewResponse,
  DeleteViewQueryParameters,
  DeleteViewQueryResponse,
  DividerBlockObjectResponse,
  EmailPropertyItemObjectResponse,
  EmbedBlockObjectResponse,
  EquationBlockObjectResponse,
  EquationRichTextItemResponse,
  FileBlockObjectResponse,
  FilesPropertyItemObjectResponse,
  FileUploadObjectResponse,
  FormulaPropertyItemObjectResponse,
  GetAsyncTaskParameters,
  GetAsyncTaskResponse,
  GetAgentParameters,
  GetAgentResponse,
  GetBlockParameters,
  GetBlockResponse,
  GetCommentParameters,
  GetCommentResponse,
  GetDatabaseParameters,
  GetDatabaseResponse,
  GetDataSourceParameters,
  GetDataSourceResponse,
  GetFileUploadParameters,
  GetFileUploadResponse,
  GetInsightsParameters,
  GetInsightsResponse,
  GetPageParameters,
  GetPagePropertyParameters,
  GetPagePropertyResponse,
  GetPageResponse,
  GetSelfParameters,
  GetSelfResponse,
  GetUserParameters,
  GetUserResponse,
  GetViewParameters,
  GetViewResponse,
  GetViewQueryResultsParameters,
  GetViewQueryResultsResponse,
  GroupObjectResponse,
  Heading1BlockObjectResponse,
  Heading2BlockObjectResponse,
  Heading3BlockObjectResponse,
  ImageBlockObjectResponse,
  LastEditedByPropertyItemObjectResponse,
  LastEditedTimePropertyItemObjectResponse,
  LinkPreviewBlockObjectResponse,
  LinkToPageBlockObjectResponse,
  ListBlockChildrenParameters,
  ListBlockChildrenResponse,
  ListCommentsParameters,
  ListCommentsResponse,
  ListCustomEmojisParameters,
  ListCustomEmojisResponse,
  CustomEmojiResponse,
  ListDatabaseViewsParameters,
  ListDatabaseViewsResponse,
  ListFileUploadsParameters,
  ListFileUploadsResponse,
  ListUsersParameters,
  ListUsersResponse,
  MeetingNotesBlockObjectResponse,
  MentionRichTextItemResponse,
  MovePageParameters,
  MovePageResponse,
  MultiSelectPropertyItemObjectResponse,
  NumberedListItemBlockObjectResponse,
  NumberPropertyItemObjectResponse,
  OauthIntrospectParameters,
  OauthIntrospectResponse,
  OauthRevokeParameters,
  OauthRevokeResponse,
  OauthTokenParameters,
  OauthTokenResponse,
  PageObjectResponse,
  ParagraphBlockObjectResponse,
  PartialBlockObjectResponse,
  PartialCommentObjectResponse,
  PartialDatabaseObjectResponse,
  PartialDataSourceObjectResponse,
  PartialDataSourceViewObjectResponse,
  PartialPageObjectResponse,
  PartialUserObjectResponse,
  PdfBlockObjectResponse,
  PeoplePropertyItemObjectResponse,
  PersonUserObjectResponse,
  PhoneNumberPropertyItemObjectResponse,
  PropertyItemListResponse,
  PropertyItemObjectResponse,
  PublicApiAsyncTaskStatusResultJsonValue,
  QueryDataSourceParameters,
  QueryDataSourceResponse,
  QueryAgentsParameters,
  QueryAgentsResponse,
  QuerySessionEventsParameters,
  QuerySessionEventsResponse,
  QuerySessionsParameters,
  QuerySessionsResponse,
  QuoteBlockObjectResponse,
  RelationPropertyItemObjectResponse,
  RetrieveSessionParameters,
  RetrieveSessionResponse,
  RichTextItemResponse,
  RichTextItemResponseCommon,
  RichTextPropertyItemObjectResponse,
  RollupPropertyItemObjectResponse,
  SearchParameters,
  SearchResponse,
  SelectPropertyItemObjectResponse,
  SendFileUploadParameters,
  SendFileUploadResponse,
  StatusPropertyItemObjectResponse,
  SyncedBlockBlockObjectResponse,
  TableBlockObjectResponse,
  TableOfContentsBlockObjectResponse,
  TableRowBlockObjectResponse,
  TemplateBlockObjectResponse,
  TextRichTextItemResponse,
  TitlePropertyItemObjectResponse,
  ToDoBlockObjectResponse,
  ToggleBlockObjectResponse,
  TranscriptionBlockObjectResponse,
  UniqueIdPropertyItemObjectResponse,
  UnsupportedBlockObjectResponse,
  UpdateBlockParameters,
  UpdateBlockResponse,
  UpdateAgentCreditLimitParameters,
  UpdateAgentCreditLimitResponse,
  UpdateAgentStatusParameters,
  UpdateAgentStatusResponse,
  UpdateCommentParameters,
  UpdateCommentResponse,
  UpdateDatabaseParameters,
  UpdateDatabaseResponse,
  UpdateDataSourceParameters,
  UpdateDataSourceResponse,
  UpdatePageParameters,
  UpdatePageResponse,
  UpdateSessionParameters,
  UpdateSessionResponse,
  UpdateSessionStreamParameters,
  UpdateSessionStreamResponse,
  UpdateViewParameters,
  UpdateViewResponse,
  UrlPropertyItemObjectResponse,
  UserObjectResponse,
  ViewQueryResponse,
  VerificationPropertyItemObjectResponse,
  VideoBlockObjectResponse,
  // Webhook payload types
  BaseWebhookPayload,
  CommentCreatedWebhookPayload,
  CommentDeletedWebhookPayload,
  CommentUpdatedWebhookPayload,
  DataSourceContentUpdatedWebhookPayload,
  DataSourceCreatedWebhookPayload,
  DataSourceDeletedWebhookPayload,
  DataSourceMovedWebhookPayload,
  DataSourceSchemaUpdatedWebhookPayload,
  DataSourceUndeletedWebhookPayload,
  DatabaseContentUpdatedWebhookPayload,
  DatabaseCreatedWebhookPayload,
  DatabaseDeletedWebhookPayload,
  DatabaseMovedWebhookPayload,
  DatabaseSchemaUpdatedWebhookPayload,
  DatabaseUndeletedWebhookPayload,
  FileUploadCompletedWebhookPayload,
  FileUploadCreatedWebhookPayload,
  FileUploadExpiredWebhookPayload,
  FileUploadUploadFailedWebhookPayload,
  PageContentUpdatedWebhookPayload,
  PageCreatedWebhookPayload,
  PageDeletedWebhookPayload,
  PageLockedWebhookPayload,
  PageMovedWebhookPayload,
  PagePropertiesUpdatedWebhookPayload,
  PageTranscriptionBlockTranscriptDeletedWebhookPayload,
  PageUndeletedWebhookPayload,
  PageUnlockedWebhookPayload,
  ViewCreatedWebhookPayload,
  ViewDeletedWebhookPayload,
  ViewUpdatedWebhookPayload,
} from "./api-endpoints"
export type AnnotationResponse = RichTextItemResponseCommon["annotations"]
export { default as Client } from "./Client"
export { LogLevel, Logger } from "./logging"
export {
  // Error codes
  NotionErrorCode,
  APIErrorCode,
  ClientErrorCode,
  // Error types
  NotionClientError,
  APIResponseError,
  UnknownHTTPResponseError,
  RequestTimeoutError,
  InvalidPathParameterError,
  // Error helpers
  isNotionClientError,
  isHTTPResponseError,
} from "./errors"
export type { RetryOptions } from "./Client"
export {
  DEFAULT_BASE_URL,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_MAX_RETRIES,
  DEFAULT_INITIAL_RETRY_DELAY_MS,
  DEFAULT_MAX_RETRY_DELAY_MS,
  MIN_VIEW_COLUMN_WIDTH,
} from "./constants"
export {
  collectPaginatedAPI,
  iteratePaginatedAPI,
  collectDataSourceTemplates,
  iterateDataSourceTemplates,
  iterateAllDataSourceRows,
  collectAllDataSourceRows,
  isFullBlock,
  isFullDataSource,
  isFullDatabase,
  isFullPage,
  isFullUser,
  isFullComment,
  isFullView,
  isFullPageOrDataSource,
  extractNotionId,
  extractDatabaseId,
  extractPageId,
  extractBlockId,
} from "./helpers"
export type {
  FullDataSourceQueryArgs,
  FullDataSourceQueryFilter,
} from "./helpers"
export { verifyWebhookSignature, signWebhookPayload } from "./webhooks"
export type { VerifyWebhookSignatureArgs } from "./webhooks"
export type { QueryMeetingNotesResponse } from "./api-endpoints/meeting-notes"
export type {
  QueryMeetingNotesParameters,
  MeetingNotesCombinatorFilter,
  MeetingNotesFilterNode,
  MeetingNotesPropertyFilter,
  MeetingNotesPropertyName,
  MeetingNotesSort,
} from "./meeting-notes"
export { meetingNotesFilterableProperties } from "./meeting-notes"
